import React, { useEffect, useState, useRef } from "react";
import {
  Alert as MuiAlert,
  Box,
  Grid,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import { spacing } from "@mui/system";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FormProvider } from "react-hook-form";
import ReplayIcon from "@mui/icons-material/Replay";
import { useDispatch } from "react-redux";

import { useDialog } from "../../../../contexts/DialogContext";
import useUser from "../../../../hooks/useUser";
import SearchableAutoComplete from "../../../../components/SearchableAutoComplete";
import useTenant from "../../../../hooks/useTenant";
import useRate from "../../../../hooks/useRate";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";

const Alert = styled(MuiAlert)(spacing);

const validationSchema = Yup.object().shape({
  plan: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required(),
  rate: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required(),
});

const ChangePlan = (props) => {
  const dispatch = useDispatch();
  const { isRenewSubmitting, updatePlan } = useUser();
  const { autoSearchRate, rateAutoSearchResults, isLoadingAutoSearch: rateLoading } = useRate();

  const { user, refreshPage } = props;
  const currentPlanPayload = {
    filters: [],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [],
  };
  const [ratePayload, setRatePayload] = useState(currentPlanPayload);
  const { tenantPlanGroupPlanAutoSearch, isLoadingAutoSearch, planGroupPlanAutoSearchResults } =
    useTenant();
  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });

  const { t } = useTranslation();

  const {
    reset,
    handleSubmit,
    watch,
    resetField,
    formState: { errors },
    control,
  } = methods;
  const { unSetDialog, openDialog } = useDialog();

  const [planPayload, setPlanPayload] = useState(currentPlanPayload);
  const [planOptions, setPlanOptions] = useState([]);
  const [rateOptions, setRateOptions] = useState([]);

  const [oldPlan, setOldPlan] = useState({
    planName: "",
    planId: 0,
  });
  const [tenantId, setTenantId] = useState(0);

  useEffect(() => {
    //console.log(ispOptions, "ispOptions", ispAutoSearchResults);
    if (planGroupPlanAutoSearchResults.data.length !== 0) {
      const results = [...planOptions, ...planGroupPlanAutoSearchResults.data];
      const filteredResults = results.filter(
        (value, index, self) => index === self.findIndex((t) => t.value === value.value),
      );
      filteredResults.sort((a, b) => a.label.localeCompare(b.label));
      setPlanOptions(filteredResults);
    }
  }, [planGroupPlanAutoSearchResults]);

  useEffect(() => {
    //console.log(ispOptions, "ispOptions", ispAutoSearchResults);
    if (rateAutoSearchResults.data.length !== 0) {
      const results = [...rateOptions, ...rateAutoSearchResults.data];
      const filteredResults = results.filter(
        (value, index, self) => index === self.findIndex((t) => t.value === value.value),
      );
      filteredResults.sort((a, b) => a.label.localeCompare(b.label));
      setRateOptions(filteredResults);
    }
  }, [rateAutoSearchResults]);

  const fetchPlans = async () => {
    await tenantPlanGroupPlanAutoSearch(user?.tenantId, planPayload);
  };

  const fetchRates = async () => {
    await autoSearchRate(ratePayload);
  };

  useEffect(() => {
    fetchPlans();
  }, [planPayload, user]);

  useEffect(() => {
    if (ratePayload.filters.length != 0) {
      fetchRates();
    }
  }, [ratePayload]);

  useEffect(() => {
    console.log(user, "useruser");
    if (Object.keys(user).length != 0 && user.userName != "") {
      const initialValues = {
        plan: {
          value: user.planId,
          label: user.planName,
        },
      };
      reset(initialValues);
      setOldPlan({
        planName: user?.planName,
        planId: user?.planId,
      });
      setTenantId(user.tenantId);
    } else {
      setPlanOptions([]);
    }
  }, [user]);

  const selectedPlan = watch("plan");

  useEffect(() => {
    if (selectedPlan && Object.keys(selectedPlan).length != 0) {
      resetField("rate", { defaultValue: "" });
      setRateOptions([]);
      setRatePayload({
        ...ratePayload,
        filters: [
          {
            fieldName: "planId",
            fieldValue: selectedPlan?.value,
            op: "eq",
            dataType: "int",
            condition: "and",
            isJoin: true,
          },
        ],
      });
    } else {
      setRateOptions([]);
    }
  }, [selectedPlan]);

  const isPlanMounted = useRef(false);

  const onSubmit = async (data) => {
    try {
      if (oldPlan.planId == data.plan.value) {
        dispatch(showErrorSnackbar("Please change the plan and update"));
      } else {
        const payload = {
          radUserId: user.radUserId,
          userName: user.userName,
          planId: data.plan.value,
          planName: data.plan.label,
          oldPlanName: oldPlan.planName,
          rateId: data.rate.value,
          rateFullName: data.rate.label,
        };
        await updatePlan(payload);
        dispatch(showSuccessSnackbar("plan changed successfully!"));
        unSetDialog("change_plan");
        if (typeof refreshPage === "function") {
          refreshPage();
        }
      }
    } catch (ex) {
      dispatch(showErrorSnackbar(ex.message));
    }
  };

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={12} className="row" pt={8}>
            <Grid item md={6}>
              <SearchableAutoComplete
                control={control}
                data={planOptions}
                label={t("select_plan")}
                payload={planPayload}
                setPayload={setPlanPayload}
                loading={isLoadingAutoSearch}
                name="plan"
                multiple={false}
                defaultValue={""}
                hasNextPage={false}
                fields={validationSchema.fields}
              />
            </Grid>
            <Grid item md={6}>
              <SearchableAutoComplete
                control={control}
                data={rateOptions}
                label={t("select_rate")}
                payload={ratePayload}
                setPayload={setRatePayload}
                loading={rateLoading}
                name="rate"
                multiple={false}
                defaultValue={""}
                hasNextPage={false}
                fields={validationSchema.fields}
              />
            </Grid>
          </Grid>
          <DialogActions>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<ReplayIcon />}
              endIcon={isRenewSubmitting ? <CircularProgress size={24} /> : null}
              disabled={false}
            >
              Save
            </Button>

            <Button variant="text" onClick={() => unSetDialog("change_plan")} color="primary">
              Close
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </React.Fragment>
  );
};
export default ChangePlan;
